import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { ArticleJsonLD } from "@/components/feature/JsonLD";
import { getArticleBySlug, getRelatedArticles, articles } from "@/mocks/articles";
import { getCategoryBySlug } from "@/mocks/categories";
import NotFound from "@/pages/NotFound";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : null;

  if (!article) {
    return <NotFound />;
  }

  const category = getCategoryBySlug(article.category);
  const relatedArticles = getRelatedArticles(article.slug, article.category, 3);

  return (
    <>
      <ScrollToTop />
      <ArticleJsonLD
        title={article.title}
        description={article.excerpt}
        url={`https://fulidaohang.com/article/${article.slug}`}
        imageUrl={article.imageUrl}
        publishedAt={article.publishedAt}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-20">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", path: "/" },
                { label: "分类浏览", path: "/category/gouwu-youhui" },
                ...(category
                  ? [{ label: category.name, path: `/category/${category.slug}` }]
                  : []),
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="w-full px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-10">
            {/* Main Content */}
            <main className="flex-1 min-w-0 max-w-3xl">
              <div className="relative w-full h-64 md:h-80 rounded-lg overflow-hidden mb-8">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="flex flex-wrap items-center gap-4 mb-6">
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="text-xs px-3 py-1.5 rounded-md bg-primary-100 text-primary-700 font-medium whitespace-nowrap hover:bg-primary-200 transition-colors"
                  >
                    {category.name}
                  </Link>
                )}
                <span className="text-xs text-foreground-500 flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.publishedAt}
                </span>
                <span className="text-xs text-foreground-500 flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  阅读时间 {article.readTime}
                </span>
              </div>

              <h1 className="font-heading text-xl md:text-3xl font-semibold text-foreground-900 mb-6 leading-tight">
                {article.title}
              </h1>

              <div className="flex flex-wrap gap-2 mb-8">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-700 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                  prose-h2:font-heading prose-h2:text-xl prose-h2:font-semibold prose-h2:text-foreground-900 prose-h2:mt-8 prose-h2:mb-4
                  prose-h3:font-heading prose-h3:text-lg prose-h3:font-semibold prose-h3:text-foreground-900 prose-h3:mt-6 prose-h3:mb-3
                  prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
                  prose-ul:my-4 prose-ul:pl-5 prose-li:text-foreground-700 prose-li:mb-2
                  prose-ol:my-4 prose-ol:pl-5 prose-li:text-foreground-700 prose-li:mb-2
                  prose-strong:text-foreground-900 prose-strong:font-semibold
                  prose-table:w-full prose-table:border-collapse prose-table:my-6
                  prose-th:bg-background-100 prose-th:p-3 prose-th:text-sm prose-th:font-medium prose-th:text-foreground-900 prose-th:border prose-th:border-background-200
                  prose-td:p-3 prose-td:text-sm prose-td:text-foreground-700 prose-td:border prose-td:border-background-200"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </main>

            {/* Sidebar */}
            <aside className="w-full lg:w-72 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">
                  相关文章
                </h3>
                <div className="flex flex-col gap-4">
                  {relatedArticles.length > 0 ? (
                    relatedArticles.map((ra) => (
                      <Link
                        key={ra.id}
                        to={`/article/${ra.slug}`}
                        className="group flex gap-3 p-3 rounded-lg border border-background-200/70 hover:border-primary-200 transition-all"
                      >
                        <div className="w-20 h-16 rounded-md overflow-hidden flex-shrink-0">
                          <img
                            src={ra.imageUrl}
                            alt={ra.title}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-xs font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 mb-1">
                            {ra.title}
                          </h4>
                          <span className="text-xs text-foreground-500">
                            {ra.publishedAt}
                          </span>
                        </div>
                      </Link>
                    ))
                  ) : (
                    <p className="text-sm text-foreground-500">暂无相关文章</p>
                  )}
                </div>

                {/* Recent Articles */}
                <div className="mt-8">
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">
                    最新文章
                  </h3>
                  <div className="flex flex-col gap-3">
                    {articles
                      .filter((a) => a.slug !== article.slug)
                      .slice(0, 5)
                      .map((a) => (
                        <Link
                          key={a.id}
                          to={`/article/${a.slug}`}
                          className="text-sm text-foreground-600 hover:text-primary-500 transition-colors line-clamp-2"
                        >
                          {a.title}
                        </Link>
                      ))}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </article>

        <Footer />
      </div>
    </>
  );
}